import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../_services/notification.service';
import { ConfirmDeleteDialogComponent } from '../gestion-usuarios/dialogs/confirm-delete-dialog.component';
import { AutofirmaLoteService } from './autofirma/autofirma-lote.service';
import {
  ComunicacionResumen,
  ComunicacionStats,
  DestinatarioMin,
  EstadoComunicacion,
  TipoEnvio,
  TIPO_ENVIO_COMUNICACION_ID,
  TIPO_ENVIO_NOTIFICACION_ID,
  etiquetaDestinatario,
  inicialesDestinatario
} from './comunicacion.model';
import { RemesaDetailDrawerComponent, RemesaDetailAction } from './drawers/remesa-detail-drawer.component';
import { RemesaFormDrawerComponent, RemesaFormResult } from './drawers/remesa-form-drawer.component';
import { GestionRemesasService } from './gestion-remesas.service';

@Component({
  selector: 'app-gestion-remesas',
  templateUrl: './gestion-remesas.component.html',
  styleUrls: ['./gestion-remesas.component.scss']
})
export class GestionRemesasComponent implements OnInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;

  displayedColumns: string[] = ['asunto', 'tipologia', 'destinatarios', 'estado', 'fecha', 'acciones'];

  remesas: ComunicacionResumen[] = [];
  stats: ComunicacionStats = { total: 0, enviadas: 0, borradores: 0, pendientesFirma: 0, programadas: 0 };
  tiposEnvio: TipoEnvio[] = [];

  filtroBusqueda = new FormControl('');
  filtroEstado = new FormControl<EstadoComunicacion | 'todos'>('todos');
  filtroTipoEnvio = new FormControl<number | 'todas'>('todas');

  page = 0;
  size = 5;
  totalElements = 0;
  loading = false;
  mensajeSpinner = '';

  readonly TIPO_COMUNICACION = TIPO_ENVIO_COMUNICACION_ID;
  readonly TIPO_NOTIFICACION = TIPO_ENVIO_NOTIFICACION_ID;
  private readonly spinnerName = 'gestion_remesas_spinner';
  private spinnerTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private service: GestionRemesasService,
    private notif: NotificationService,
    private dialog: MatDialog,
    private paginatorIntl: MatPaginatorIntl,
    private spinner: NgxSpinnerService,
    private autofirma: AutofirmaLoteService,
    private translate: TranslateService
  ) {
    this.traduccionPaginator();
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private tipoLabel(tipo?: number | TipoEnvio | null, capital = true): string {
    const id = typeof tipo === 'number' ? tipo : tipo?.id;
    const key = id === this.TIPO_NOTIFICACION
      ? 'REMESAS.TIPOLOGIA.NOTIFICACION'
      : 'REMESAS.TIPOLOGIA.COMUNICACION';
    const label = this.t(key);
    return capital ? label : label.toLowerCase();
  }

  ngOnInit(): void {
    this.cargaTipos();
    this.cargaStats();
    this.cargaRemesas();

    this.filtroBusqueda.valueChanges.pipe(debounceTime(350)).subscribe(() => {
      this.page = 0;
      this.cargaRemesas();
    });
    this.filtroEstado.valueChanges.subscribe(() => { this.page = 0; this.cargaRemesas(); });
    this.filtroTipoEnvio.valueChanges.subscribe(() => { this.page = 0; this.cargaRemesas(); });
  }

  cargaTipos(): void {
    this.service.tipos().subscribe({
      next: (tipos) => { this.tiposEnvio = tipos || []; },
      error: () => {}
    });
  }

  cargaStats(): void {
    this.service.stats().subscribe({
      next: (s) => { this.stats = s; },
      error: () => {}
    });
  }

  cargaRemesas(): void {
    this.loading = true;
    this.service.list({
      page: this.page,
      size: this.size,
      search: this.filtroBusqueda.value || '',
      estado: this.filtroEstado.value || 'todos',
      tipoEnvio: this.filtroTipoEnvio.value || 'todas'
    }).subscribe({
      next: (resp) => {
        this.remesas = resp.data || [];
        this.totalElements = resp.totalElements || 0;
        this.loading = false;
      },
      error: () => {
        this.notif.error(this.t('COMMON.ERROR'), this.t('REMESAS.MSG.CARGAR_LISTA'), 3000);
        this.loading = false;
      }
    });
  }

  onPageChange(ev: PageEvent): void {
    this.page = ev.pageIndex;
    this.size = ev.pageSize;
    this.cargaRemesas();
  }

  // ---------- Apertura de drawers ----------
  abrirCrear(): void {
    const ref = this.dialog.open(RemesaFormDrawerComponent, {
      data: { tiposEnvio: this.tiposEnvio },
      panelClass: 'gr-drawer-panel',
      position: { right: '0', top: '0' },
      maxWidth: '100vw',
      maxHeight: '100vh'
    });
    ref.afterClosed().subscribe((result: RemesaFormResult | null) => this.handleFormResult(result));
  }

  abrirEditarBorrador(id: number, tipoHint?: TipoEnvio): void {
    this.service.getById(id).subscribe({
      next: (com) => {
        const ref = this.dialog.open(RemesaFormDrawerComponent, {
          data: { comunicacion: com, tiposEnvio: this.tiposEnvio },
          panelClass: 'gr-drawer-panel',
          position: { right: '0', top: '0' },
          maxWidth: '100vw',
          maxHeight: '100vh'
        });
        ref.afterClosed().subscribe((result: RemesaFormResult | null) => this.handleFormResult(result));
      },
      error: () => this.notif.error(
        this.t('COMMON.ERROR'),
        this.t('REMESAS.MSG.CARGAR_TIPO', { tipo: this.tipoLabel(tipoHint, false) }),
        3000
      )
    });
  }

  verDetalle(remesa: ComunicacionResumen): void {
    this.service.getById(remesa.id).subscribe({
      next: (com) => {
        const ref = this.dialog.open(RemesaDetailDrawerComponent, {
          data: { comunicacion: com },
          panelClass: 'gr-drawer-panel',
          position: { right: '0', top: '0' },
          maxWidth: '100vw',
          maxHeight: '100vh'
        });
        ref.afterClosed().subscribe((action: RemesaDetailAction | null) => {
          if (!action) return;
          if (action === 'edit') this.abrirEditarBorrador(remesa.id, remesa.tipoEnvio);
          else if (action === 'duplicate') this.duplicar(remesa);
          else if (action === 'delete') this.confirmarEliminar(remesa);
          else if (action === 'firmar-enviar') this.firmarYEnviarPendiente(com);
        });
      },
      error: () => this.notif.error(
        this.t('COMMON.ERROR'),
        this.t('REMESAS.MSG.CARGAR_TIPO', { tipo: this.tipoLabel(remesa.tipoEnvio, false) }),
        3000
      )
    });
  }

  // ---------- Acciones ----------
  private handleFormResult(result: RemesaFormResult | null): void {
    if (!result) return;
    const tipo = this.tipoLabel(result.payload.tipoEnvio, false);
    const Tipo = this.tipoLabel(result.payload.tipoEnvio);
    const spinnerMsg = result.action === 'send'
      ? this.t('REMESAS.MSG.ENVIANDO_TIPO', { tipo })
      : result.action === 'schedule'
        ? this.t('REMESAS.MSG.PROGRAMANDO')
        : result.action === 'request-sign'
          ? this.t('REMESAS.MSG.SOLICITANDO_FIRMA')
          : this.t('REMESAS.MSG.GUARDANDO_BORRADOR');
    this.mostrarSpinnerMsg(spinnerMsg);

    this.service.save(result.payload).subscribe({
      next: (resp: any) => {
        const newId = resp?.remesa?.id ?? resp?.comunicacion?.id ?? resp?.id;
        if (result.action === 'draft') {
          this.ocultarSpinner();
          this.notif.success(this.t('COMMON.DONE'), this.t('REMESAS.MSG.BORRADOR_GUARDADO'), 2800);
          this.refresh();
        } else if (result.action === 'request-sign' && newId) {
          if (!result.firmanteId) {
            this.ocultarSpinner();
            this.notif.error(this.t('COMMON.ERROR'), this.t('REMESAS.MSG.FALTA_FIRMANTE'), 3500);
            this.refresh();
            return;
          }
          this.service.solicitarFirma(newId, result.firmanteId).subscribe({
            next: () => {
              this.ocultarSpinner();
              this.notif.success(this.t('COMMON.DONE'), this.t('REMESAS.MSG.FIRMA_SOLICITADA'), 2800);
              this.refresh();
            },
            error: (err) => {
              this.ocultarSpinner();
              this.notifError(err, this.t('REMESAS.MSG.ERROR_SOLICITAR_FIRMA', { tipo }));
              this.refresh();
            }
          });
        } else if ((result.action === 'send' || result.action === 'schedule') && newId) {
          this.continuarTrasGuardar(result, newId, tipo, Tipo);
        } else {
          this.ocultarSpinner();
          this.refresh();
        }
      },
      error: (err) => {
        this.ocultarSpinner();
        this.notifError(err, this.t('REMESAS.MSG.ERROR_GUARDAR', { tipo }));
        this.refresh();
      }
    });
  }

  private async firmarYEnviarPendiente(com: ComunicacionResumen & { fechaFirma?: any }): Promise<void> {
    const tipo = this.tipoLabel(com.tipoEnvio, false);
    const Tipo = this.tipoLabel(com.tipoEnvio);
    const yaFirmada = !!com.fechaFirma;
    const esNotificacion = com.tipoEnvio?.id === this.TIPO_NOTIFICACION;
    const result: RemesaFormResult = {
      action: 'send',
      payload: { concepto: com.concepto, tipoEnvio: com.tipoEnvio?.id ?? 1, destinatarios: [] },
      firmarAntesEnviar: esNotificacion && !yaFirmada
    };
    this.mostrarSpinnerMsg(yaFirmada ? this.t('REMESAS.MSG.ENVIANDO') : this.t('REMESAS.MSG.PREPARANDO_DOCS'), true);
    await this.continuarTrasGuardar(result, com.id, tipo, Tipo);
  }

  private async continuarTrasGuardar(
    result: RemesaFormResult,
    newId: number,
    tipo: string,
    Tipo: string
  ): Promise<void> {
    try {
      if ((result.firmarAntesEnviar || result.payload.firmarAntesEnviar)
          && result.payload.tipoEnvio === this.TIPO_NOTIFICACION) {
        this.mostrarSpinnerMsg(this.t('REMESAS.MSG.PREPARANDO_DOCS'), true);
        const prep = await firstValueFrom(this.service.prepararDocumentosFirma(newId));
        const docs = prep?.documentos || [];
        if (!docs.length) {
          throw new Error(this.t('REMESAS.MSG.SIN_DOCS_FIRMA'));
        }
        this.mostrarSpinnerMsg(this.t('REMESAS.MSG.ABRIENDO_AUTOFIRMA'), true);
        const firmados = await this.autofirma.firmarDocumentos(docs);
        this.mostrarSpinnerMsg(this.t('REMESAS.MSG.GUARDANDO_FIRMADOS'), true);
        await firstValueFrom(this.service.guardarDocumentosFirmados(newId, firmados));
      }

      if (result.action === 'send') {
        this.mostrarSpinnerMsg(this.t('REMESAS.MSG.ENVIANDO_NOTIFICA'));
        await firstValueFrom(this.service.send(newId));
        this.ocultarSpinner();
        this.notif.success(this.t('COMMON.DONE'), this.t('REMESAS.MSG.ENVIADA', { tipo: Tipo }), 2800);
      } else if (result.action === 'schedule' && result.fechaProgramada) {
        this.mostrarSpinnerMsg(this.t('REMESAS.MSG.PROGRAMANDO'));
        await firstValueFrom(this.service.schedule(newId, result.fechaProgramada));
        this.ocultarSpinner();
        this.notif.success(this.t('COMMON.DONE'), this.t('REMESAS.MSG.PROGRAMADA', { tipo: Tipo }), 2800);
      }
      this.refresh();
    } catch (err) {
      this.ocultarSpinner();
      this.notifError(err, result.firmarAntesEnviar
        ? this.t('REMESAS.MSG.ERROR_FIRMAR_ENVIAR', { tipo })
        : this.t('REMESAS.MSG.ERROR_ENVIAR', { tipo }));
      this.refresh();
    }
  }

  mostrarSpinnerMsg(mensaje: string, persistente = false): void {
    this.mensajeSpinner = mensaje;
    this.spinner.show(this.spinnerName);
    if (this.spinnerTimer) {
      clearTimeout(this.spinnerTimer);
      this.spinnerTimer = null;
    }
    if (!persistente) {
      this.spinnerTimer = setTimeout(() => {
        this.spinner.hide(this.spinnerName);
      }, 60000);
    }
  }

  ocultarSpinner(): void {
    if (this.spinnerTimer) {
      clearTimeout(this.spinnerTimer);
      this.spinnerTimer = null;
    }
    this.spinner.hide(this.spinnerName);
  }

  duplicar(remesa: ComunicacionResumen | number): void {
    if (typeof remesa !== 'number' && remesa.estado === 'PENDIENTE_FIRMA') {
      this.notif.warning(this.t('COMMON.WARNING'), this.t('REMESAS.MSG.NO_DUPLICAR_FIRMA'), 3000);
      return;
    }
    const id = typeof remesa === 'number' ? remesa : remesa.id;
    const tipoHint = typeof remesa === 'number' ? undefined : remesa.tipoEnvio;
    const tipo = this.tipoLabel(tipoHint, false);
    const Tipo = this.tipoLabel(tipoHint);
    this.service.duplicate(id).subscribe({
      next: () => { this.notif.success(this.t('COMMON.DONE'), this.t('REMESAS.MSG.DUPLICADA', { tipo: Tipo }), 2800); this.refresh(); },
      error: (err) => this.notifError(err, this.t('REMESAS.MSG.ERROR_DUPLICAR', { tipo }))
    });
  }

  confirmarEliminar(remesa: ComunicacionResumen): void {
    if (remesa.estado !== 'BORRADOR' && remesa.estado !== 'PENDIENTE_FIRMA') {
      this.notif.warning(this.t('COMMON.WARNING'), this.t('REMESAS.MSG.SOLO_ELIMINAR_BORRADOR'), 3000);
      return;
    }
    const tipo = this.tipoLabel(remesa.tipoEnvio, false);
    const Tipo = this.tipoLabel(remesa.tipoEnvio);
    const ref = this.dialog.open(ConfirmDeleteDialogComponent, {
      data: {
        title: this.t('REMESAS.MSG.ELIMINAR_TIPO', { tipo }),
        message: this.t('REMESAS.MSG.CONFIRMAR_ELIMINAR', { tipo, concepto: remesa.concepto }),
        confirmLabel: this.t('REMESAS.MSG.ELIMINAR')
      },
      panelClass: 'gu-dialog-panel'
    });
    ref.afterClosed().subscribe((ok) => {
      if (!ok) return;
      this.service.delete(remesa.id).subscribe({
        next: () => { this.notif.success(this.t('COMMON.DONE'), this.t('REMESAS.MSG.ELIMINADA', { tipo: Tipo }), 2800); this.refresh(); },
        error: (err) => this.notifError(err, this.t('REMESAS.MSG.ERROR_ELIMINAR', { tipo }))
      });
    });
  }

  private refresh(): void { this.cargaStats(); this.cargaRemesas(); }
  private notifError(err: any, fallback: string): void {
    const msg = err?.error?.message || err?.message || fallback;
    this.notif.error(this.t('COMMON.ERROR'), msg, 3500);
  }

  // ---------- Helpers UI ----------
  estadoLabel(estado: string): string {
    if (estado === 'ENVIADA') return this.t('REMESAS.ESTADO.ENVIADA');
    if (estado === 'PROGRAMADA') return this.t('REMESAS.ESTADO.PROGRAMADA');
    if (estado === 'PENDIENTE_FIRMA') return this.t('REMESAS.ESTADO.PENDIENTE_FIRMA');
    return this.t('REMESAS.ESTADO.BORRADOR');
  }

  tipologiaLabel(tipoEnvio: TipoEnvio | undefined): string {
    if (!tipoEnvio) return '';
    return this.tipoLabel(tipoEnvio);
  }

  esComunicacion(tipoEnvio: TipoEnvio | undefined): boolean {
    return tipoEnvio?.id === this.TIPO_COMUNICACION;
  }

  esNotificacion(tipoEnvio: TipoEnvio | undefined): boolean {
    return tipoEnvio?.id === this.TIPO_NOTIFICACION;
  }

  preview(cuerpo: string | undefined, max = 60): string {
    if (!cuerpo) return '';
    return cuerpo.length > max ? cuerpo.substring(0, max) + '…' : cuerpo;
  }

  iniciales(d: DestinatarioMin): string {
    return inicialesDestinatario(d);
  }

  etiquetaDest(d: DestinatarioMin): string {
    return etiquetaDestinatario(d);
  }

  formatFecha(value: string | number[] | undefined): string {
    if (!value) return '';
    let date: Date;
    if (Array.isArray(value)) {
      const [y, m, d] = value;
      date = new Date(y, (m || 1) - 1, d || 1);
    } else {
      date = new Date(value);
    }
    if (isNaN(date.getTime())) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
  }

  private traduccionPaginator(): void {
    this.paginatorIntl.itemsPerPageLabel = this.t('PAGINATOR.ITEMS_PER_PAGE');
    this.paginatorIntl.firstPageLabel = '';
    this.paginatorIntl.previousPageLabel = '';
    this.paginatorIntl.nextPageLabel = '';
    this.paginatorIntl.lastPageLabel = '';
    this.paginatorIntl.getRangeLabel = (page, pageSize, length) => {
      if (length === 0 || pageSize === 0) return this.t('PAGINATOR.RANGE_EMPTY', { length });
      const startIndex = page * pageSize;
      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
      return this.t('PAGINATOR.RANGE', { start: startIndex + 1, end: endIndex, length });
    };
  }
}
